import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components';
import { Button } from 'components/form';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { TUserApiResponse } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

import Header from '../Header';
import SocietyCard, { SocietyCardProps } from '../SocietyCard';
import StepHeading from '../StepHeading';
import styles from './Step3.module.scss';

interface Step3Props {
  progress: number;
  setFlowState: (flowState: string) => void;
}

export const Step3: React.FC<Step3Props> = ({ progress, setFlowState }) => {
  const { updateUserApiResponse, managerSociety, manager } = useAuth();

  const [t] = useTranslation('onboarding.manager');

  const router = useRouter();
  const show_custom_link_screen =
    manager?.linktree_url !== null ||
    managerSociety?.custom_links?.data.length === 0;

  const [similarSocieties, setSimilarSocieties] =
    useState<Array<StudentSocietyAttributes> | null>(null);

  const [isEventExist, setIsEventExist] = React.useState(false);

  useEffect(() => {
    if (managerSociety === null) return;
    const { flagship_events } = managerSociety;

    setIsEventExist(!!flagship_events?.data.length);
  }, [managerSociety]);

  useEffect(() => {
    if (managerSociety?.slug) {
      const { slug } = managerSociety;
      const similarSocietiesPathname = `${API_ROUTES.STUDENT_SOCIETIES}/${slug}${API_ROUTES.SIMILAR_STUDENT_SOCIETIES}?with_details=false&page=1&per_page=10`;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      apiInstance.get(similarSocietiesPathname).then((res: any) => {
        const list = parseArrayResponse<StudentSocietyAttributes>(res);
        setSimilarSocieties(list);
      });
    }
  }, [managerSociety?.slug]);

  const handleNext = async () => {
    const flow_state = isEventExist
      ? ONBOARDING_FLOW_STATE.COMPLETE
      : ONBOARDING_FLOW_STATE.POST_4;
    const manager = { flow_state };
    const userResponse = await apiInstance.patch<TUserApiResponse>(
      API_ROUTES.MANAGER_USER,
      {
        manager,
      }
    );

    updateUserApiResponse(userResponse.data);
    setFlowState(flow_state);
  };

  const getCards = useMemo(() => {
    if (similarSocieties === null) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    } else {
      return similarSocieties.map((society) => {
        const university = parseResponse<{ name: string }>(society.university);
        const societyProp: SocietyCardProps = {
          id: society.id,
          name: society.name,
          icon_url: society.icon_url,
          url: society.public_url,
          university: university.name,
          is_verified: society.is_verified,
          huzzle_verified: society.huzzle_verified,
        };
        return <SocietyCard {...societyProp} key={society.id} />;
      });
    }
  }, [similarSocieties]);

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          const flowState = show_custom_link_screen
            ? ONBOARDING_FLOW_STATE.POST_2
            : ONBOARDING_FLOW_STATE.POST_1;
          setFlowState(flowState);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-3.header.title')}
          desc={t('step-3.header.desc')}
          desc2={t('step-3.header.desc-2')}
        />

        <div>
          <Button
            type="button"
            color="secondary"
            className={cn(styles.yesNoButton, styles.button)}
            onClick={() => {
              const win = window.open(
                `${PAGE_ROUTES.SOCIETIES}/${managerSociety?.slug}`,
                '_blank'
              );
              win?.focus();
            }}
            isFullWidth
          >
            {t('step-3.view-profile')}
          </Button>
          <Button
            type="button"
            color="primary"
            className={styles.button}
            onClick={() => {
              router.push(
                `${PAGE_ROUTES.SOCIETY_PROFILE_EDIT}?onboarding=true`
              );
            }}
            isFullWidth
          >
            {t('step-3.complete-profile')}
          </Button>
          <Button
            type="button"
            color="black"
            onClick={handleNext}
            variant="text"
            isFullWidth
            borderless
            className={styles.button}
          >
            {t('do-later')}
          </Button>
        </div>

        <div className={styles.divider}>
          <hr />
        </div>

        <StepHeading desc={t('step-3.header.desc-3')} />

        {getCards}
      </div>
    </>
  );
};
